import type { ChangeEvent, FC, MouseEvent } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Divider,
  Grid,
  InputAdornment,
  Link,
  Rating,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import SearchIcon from '../../../icons/Search';
import Scrollbar from '../../Scrollbar';
import { Identifier, ItemDescription } from 'src/contract-types/Store';
import { Tx, TxStats } from 'src/contract-types/Transactions';
import { getTokenIcon, nominalAmount, valuesToNamed } from 'src/lib/Value';
import TxStatus from './TxStatus';
import ArrowLeft from 'src/icons/ArrowLeft';
import AcademicCap from 'src/icons/AcademicCap';
import Minus from 'src/icons/Minus';
import dataVault from '../../../lib/DataVault';
import { FileSystem } from '../../../contract-types/Store';
import { formatDate } from 'src/lib/Utils';
import Loading from '../../../icons/Loading';
import Highlighted from '../../Highlighted';
import jp from 'jsonpath';
import { customerApi } from '../../../__fakeApi__/customerApi';
import useMounted from '../../../hooks/useMounted';
import { IconButton, Zoom } from '@mui/material';

interface SortOption {
  field: string;
  label: string;
  desc: boolean;
}

const PAGE_SIZES = [50, 100, 200, 500];

interface Props {
  txBuyerStats: TxStats | null,
  refresh: () => void,
}

const sortOptions: SortOption[] = [
  {
    label: 'Date (newest)',
    field: '$.timestamp',
    desc: true,
  },
  {
    label: 'Date (oldest)',
    field: '$.timestamp',
    desc: false,
  },
  {
    label: 'Price (highest first)',
    field: '$.price.amount',
    desc: true,
  },
  {
    label: 'Price (lowest first)',
    field: '$.price.amount',
    desc: false,
  },
  {
    label: 'Status',
    field: '$.status',
    desc: false,
  },
  {
    label: 'Name (A-Z)',
    field: 'name',
    desc: false,
  },
  {
    label: 'Name (Z-A)',
    field: 'name',
    desc: true,
  },
];

const applyFilters = (
  txs: [Identifier, Tx, ItemDescription][],
  currentTab: string,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  query: string,
): [Identifier, Tx, ItemDescription][] => txs?.filter(([, tx, itemDescription]) => {
  let pass = (currentTab === 'All') ? true : tx.status === currentTab;
  if (pass && query.trim() !== '') {
    pass = itemDescription.meta.find(([key]) => key === 'title')[1].toLowerCase().indexOf(query.toLowerCase()) !== -1;
  }
  return pass;
});

const applySort = (txs: [Identifier, Tx, ItemDescription][], sort: SortOption): [Identifier, Tx, ItemDescription][] => txs?.sort((a, b) => {
  let diff;
  switch (sort.field) {
    case 'name':
      // @ts-ignore
      diff = a[2].meta.find(([key]) => key === 'title')[1].localeCompare(b[2].meta.find(([key]) => key === 'title')[1]);
      break;
    default:
      const x = jp.query(a[1], sort.field)[0];
      const y = jp.query(b[1], sort.field)[0];
      // @ts-ignore
      // eslint-disable-next-line no-restricted-globals
      diff = (!isNaN(x) && !isNaN(y)) ? x - y : (x as string).localeCompare(y);
      break;
  }

  return sort.desc ? -diff : diff;
});

const Txs: FC<Props> = ({ txBuyerStats, refresh, ...other }: Props) => {
  const mounted = useMounted();
  const [currentTab, setCurrentTab] = useState<string>('All');
  const [page, setPage] = useState<number>(0);
  const [limit, setLimit] = useState<number>(PAGE_SIZES[2]);
  const [txs, setTxs] = useState<[Identifier, Tx, ItemDescription][] | null>(null);
  const [query, setQuery] = useState<string>('');
  const [sort, setSort] = useState<SortOption>(sortOptions[0]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const closeError = () => {
    setError(null);
  };
  const closeSuccess = () => {
    setSuccess(null);
  };

  const getTxs = async (_page: number, _limit: number) => {
    try {
      // console.log(_page, _limit);
      const data = await customerApi.getBuyerTxs(_page, _limit);
      // console.log(data);

      if (mounted.current) {
        setTxs(data.StoreGetBuyerTxs.txs);
      }
    } catch (err) {
      if (mounted.current) {
        console.error(err);
        setError(err.message);
      }
    }
  };

  const getTxsCB = useCallback(async () => getTxs(page, limit), [mounted]);

  const refund = async (cid: string, filesystem: FileSystem, title: string) => {
    try {
      await dataVault.storeReturnUnapprovedItem(cid, filesystem);
      setSuccess(`Successfully refunded ${title}`);
      setTxs(null);
      getTxs(page, limit);
      refresh();
    } catch (err) {
      console.error(err);
      setError(err.message);
    }
  };

  const feedback = async (cid: string, filesystem: FileSystem, title: string, rating: number) => {
    try {
      await dataVault.storeCompleteTx(cid, filesystem, rating);
      setSuccess(`Successfully left ${rating * 100}% feedback to ${title}`);
      setTxs(null);
      getTxs(page, limit);
      refresh();
    } catch (err) {
      console.error(err);
      setError(err.message);
    }
  };

  const dispute = async (cid: string, filesystem: FileSystem, title: string) => {
    try {
      await dataVault.storeDisputeTx(cid, filesystem);
      setSuccess(`Successfully disputed ${title}`);
      setTxs(null);
      getTxs(page, limit);
      refresh();
    } catch (err) {
      console.error(err);
      setError(err.message);
    }
  };

  const init = useCallback(async () => {
    await getTxsCB();
  }, [mounted]);

  useEffect(() => {
    init();
  }, [init]);

  const handleTabsChange = (event: ChangeEvent<{}>, value: string): void => {
    setCurrentTab(value);
  };

  const handleQueryChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setQuery(event.target.value);
  };

  const handleSortChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setSort(sortOptions[event.target.value]);
  };

  const handlePageChange = (event: MouseEvent<HTMLButtonElement> | null, newPage: number): void => {
    setPage(newPage);
    setTxs(null);
    getTxs(newPage, limit);
  };

  const handleLimitChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const newLimit = parseInt(event.target.value, 10);
    setLimit(newLimit);
    setTxs(null);
    getTxs(page, newLimit);
  };

  const tabs = [
    {
      label: `All (${txBuyerStats ? Object.keys(txBuyerStats).reduce((acc, key) => Number(txBuyerStats[key]) + acc, 0) : '-'})`,
      value: 'All'
    },
    {
      label: `Pending (${txBuyerStats ? txBuyerStats.pending_count : '-'})`,
      value: 'pending'
    },
    {
      label: `Approved (${txBuyerStats ? txBuyerStats.approved_count : '-'})`,
      value: 'approved'
    },
    {
      label: `In Dispute (${txBuyerStats ? txBuyerStats.disputed_count : '-'})`,
      value: 'dispute'
    },
    {
      label: `Refunded (${txBuyerStats ? txBuyerStats.refunded_count : '-'})`,
      value: 'refunded'
    },
    {
      label: `Complete (${txBuyerStats ? txBuyerStats.complete_count : '-'})`,
      value: 'complete'
    },
  ];

  return (
    <>
      <Card {...other}>
        <Grid
          container
          justifyContent="space-between"
        >
          <Grid
            item
            xs={11}
          >
            <Typography
              color="textPrimary"
              variant="h5"
              padding={2}
            >
              Purchases
            </Typography>
          </Grid>
          <Grid item>
            {!txs
              && <Loading />}
          </Grid>
          <Grid
            item
            xs={12}
          >
            <Tabs
              indicatorColor="primary"
              onChange={handleTabsChange}
              scrollButtons="auto"
              textColor="primary"
              value={currentTab}
              variant="scrollable"
            >
              {tabs.map((tab) => (
                <Tab
                  key={tab.value}
                  label={tab.label}
                  value={tab.value}
                />
              ))}
            </Tabs>
            <Divider />
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                flexWrap: 'wrap',
                m: -1,
                p: 2
              }}
            >
              <Box
                sx={{
                  m: 1,
                  maxWidth: '100%',
                  width: 500
                }}
              >
                <TextField
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    )
                  }}
                  onChange={handleQueryChange}
                  placeholder="Search transactions"
                  value={query}
                  variant="outlined"
                />
              </Box>
              <Box
                sx={{
                  m: 1,
                  width: 240
                }}
              >
                <TextField
                  label="Sort By"
                  name="sort"
                  onChange={handleSortChange}
                  select
                  SelectProps={{ native: true }}
                  value={sortOptions.indexOf(sort)}
                  variant="outlined"
                >
                  {sortOptions.map((option, index) => (
                    <option
                      key={index}
                      value={index}
                    >
                      {option.label}
                    </option>
                  ))}
                </TextField>
              </Box>
            </Box>
            <Scrollbar style={{ height: 'inherit' }}>
              <Box sx={{ minWidth: 700 }}>
                <Table>
                  {txs?.length === 0
                    && (
                      <Typography
                        color="textPrimary"
                        variant="subtitle2"
                        align="center"
                      >
                        No transactions yet..
                      </Typography>
                    )}
                  {txs?.length > 0
                    && (
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            Date
                          </TableCell>
                          <TableCell>
                            Name
                          </TableCell>
                          <TableCell>
                            Price
                          </TableCell>
                          <TableCell align="center">
                            Status
                          </TableCell>
                          <TableCell align="center">
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                    )}
                  <TableBody>
                    {applySort(applyFilters(txs, currentTab, query), sort)?.map(([identifier, tx, itemDescription]) => {
                      const namedValue = valuesToNamed(tx.price);
                      return (
                        <TableRow
                          hover
                          key={identifier.id}
                        >
                          <TableCell>
                            {formatDate(tx.timestamp)}
                          </TableCell>
                          <TableCell>
                            <Link
                              color="inherit"
                              component={RouterLink}
                              to={`/item/${identifier.filesystem}/${identifier.id}`}
                              variant="subtitle2"
                            >
                              <Highlighted
                                text={itemDescription.meta.find(([key]) => key === 'title')[1]}
                                highlight={query}
                              />
                            </Link>
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                alignItems: 'center',
                                display: 'flex'
                              }}
                            >
                              <Avatar
                                src={getTokenIcon(namedValue)}
                                sx={{
                                  height: 42,
                                  width: 42
                                }}
                              >
                                {namedValue.name}
                              </Avatar>
                              <Box sx={{ ml: 1 }}>
                                <Typography
                                  color="textPrimary"
                                  variant="subtitle2"
                                >
                                  {nominalAmount(namedValue.value)}
                                </Typography>
                                <Typography
                                  color="textSecondary"
                                  variant="body2"
                                >
                                  {namedValue.name}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <TxStatus
                              entity="buyer"
                              status={tx.status}
                            />
                          </TableCell>
                          {(() => {
                            switch (tx.status) {
                              case 'pending':
                                return (
                                  <TableCell align="center">
                                    <Tooltip
                                      title="Rollback the transaction and get fully refunded"
                                      color="primary"
                                      arrow
                                      placement="bottom-start"
                                      TransitionComponent={Zoom}
                                      sx={{ margin: 0, padding: 0 }}
                                    >
                                      <IconButton
                                        disableFocusRipple
                                        disableRipple
                                      >
                                        <Button
                                          color="primary"
                                          endIcon={<ArrowLeft />}
                                          size="small"
                                          variant="contained"
                                          onClick={() => refund(identifier.id, identifier.filesystem, itemDescription.meta.find(([key]) => key === 'title')[1])}
                                        >
                                          Refund
                                        </Button>
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                );
                              case 'approved':
                                return (
                                  <TableCell align="right">
                                    <Grid
                                      container
                                    >
                                      {itemDescription.disputes_disabled === false && (
                                        <Grid
                                          item
                                          md={6}
                                        >
                                          <Tooltip
                                            title="If you are sure that the content you bought does NOT match the content's description, then dispute the transaction. Once the curator will solve the dispute in your favor you will be refunded."
                                            color="error.main"
                                            arrow
                                            placement="bottom-start"
                                            TransitionComponent={Zoom}
                                            sx={{ margin: 0, padding: 0 }}
                                          >
                                            <IconButton
                                              disableFocusRipple
                                              disableRipple
                                            >
                                              <Button
                                                sx={{
                                                  backgroundColor: 'error.main',
                                                  mr: 3,
                                                  '&:hover': {
                                                    backgroundColor: 'error.dark'
                                                  }
                                                }}
                                                endIcon={<AcademicCap />}
                                                size="small"
                                                variant="contained"
                                                onClick={() => dispute(identifier.id, identifier.filesystem, itemDescription.meta.find(([key]) => key === 'title')[1])}
                                              >
                                                Dispute
                                              </Button>
                                            </IconButton>
                                          </Tooltip>
                                        </Grid>
                                      )}
                                      <Grid
                                        item
                                      >
                                        <Tooltip
                                          title="If you are sure that the content you bought matches the content's description, then rate the content in terms of quality to get back your collateral"
                                          arrow
                                          placement="bottom-start"
                                          TransitionComponent={Zoom}
                                          sx={{ margin: 0, padding: 0 }}
                                        >
                                          <IconButton
                                            disableFocusRipple
                                            disableRipple
                                          >
                                            <Rating
                                              onChange={(e, rating) => feedback(identifier.id, identifier.filesystem, itemDescription.meta.find(([key]) => key === 'title')[1], rating * 0.2)}
                                            />
                                          </IconButton>
                                        </Tooltip>
                                      </Grid>
                                    </Grid>
                                  </TableCell>
                                );
                              default:
                                return (<TableCell align="center"><Minus fontSize="small" /></TableCell>);
                            }
                          })()}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </Scrollbar>
            <TablePagination
              component="div"
              count={-1}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleLimitChange}
              page={page}
              rowsPerPage={limit}
              rowsPerPageOptions={PAGE_SIZES}
            />
          </Grid>
        </Grid>
      </Card>
      <Snackbar
        open={error !== null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        autoHideDuration={6000}
        onClose={closeError}
      >
        <Alert
          severity="error"
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>
      <Snackbar
        open={success !== null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        autoHideDuration={6000}
        onClose={closeSuccess}
      >
        <Alert
          severity="success"
          sx={{ width: '100%' }}
        >
          {success}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Txs;
